import * as React from 'react'

import { authApi } from '@/lib/api'
import { debugLogin } from '@/lib/api/endpoints/debug'
import type { LoginResponse } from '@/lib/api/types'

import {
  clearAdminSession,
  clearClubSession,
  clearScoreSession,
  readAdminSession,
  readClubSession,
  updateScoreSession,
  writeAdminSession,
  writeClubSession,
  type AdminSession,
  type ClubSession,
} from './session-store'

export interface ClubAuth {
  accessToken: string
  providerSession: string
  scoreSession: string | null
}

interface AuthState {
  club: ClubSession | null
  admin: AdminSession | null
  scoreReauthOpen: boolean
  scoreReauthResolver: ((token: string | null) => void) | null
}

type AuthAction =
  | { type: 'set-club'; session: ClubSession | null }
  | { type: 'set-admin'; session: AdminSession | null }
  | {
      type: 'open-score-reauth'
      resolve: (token: string | null) => void
    }
  | { type: 'close-score-reauth' }

function reducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'set-club':
      return { ...state, club: action.session }
    case 'set-admin':
      return { ...state, admin: action.session }
    case 'open-score-reauth':
      return {
        ...state,
        scoreReauthOpen: true,
        scoreReauthResolver: action.resolve,
      }
    case 'close-score-reauth':
      return { ...state, scoreReauthOpen: false, scoreReauthResolver: null }
  }
}

export interface AuthContextValue {
  club: ClubSession | null
  admin: AdminSession | null

  loginClub: (input: {
    clubname: string
    username: string
    password: string
  }) => Promise<LoginResponse>

  logoutClub: () => void

  /**
   * Local-dev-only: logs in as the test club through the real auth pipeline
   * against emjogo-mock, and auto-completes Score auth against score-mock.
   */
  loginDebugTestClub: () => Promise<LoginResponse>

  loginAdmin: (input: { username: string; password: string }) => Promise<void>
  logoutAdmin: () => void

  setScoreSession: (token: string, expiresAt: string | null) => ClubSession | null
  clearScoreSession: () => void

  /**
   * Opens the Score re-auth modal and resolves once the user supplies a fresh
   * session token (or cancels, resolving with null).
   */
  requestScoreReauth: () => Promise<string | null>

  /**
   * Internal: resolves an open re-auth request (called by <ScoreReauthDialog>).
   */
  resolveScoreReauth: (token: string | null) => void

  /**
   * Convenience for API calls that need the club bearer + headers bundle.
   */
  clubAuth: () => ClubAuth | null

  /**
   * Whether the Score re-auth modal is open (for <ScoreReauthDialog>).
   */
  scoreReauthOpen: boolean
}

const AuthContext = React.createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = React.useReducer(reducer, null, () => ({
    club: readClubSession(),
    admin: readAdminSession(),
    scoreReauthOpen: false,
    scoreReauthResolver: null,
  }))

  const setClub = React.useCallback((session: ClubSession | null) => {
    if (session) writeClubSession(session)
    else clearClubSession()
    dispatch({ type: 'set-club', session })
  }, [])

  const setAdmin = React.useCallback((session: AdminSession | null) => {
    if (session) writeAdminSession(session)
    else clearAdminSession()
    dispatch({ type: 'set-admin', session })
  }, [])

  const applyLoginResponse = React.useCallback(
    (response: LoginResponse) => {
      const session: ClubSession = {
        accessToken: response.access_token,
        clubname: response.clubname,
        expiresAt: response.expires_at,
        providerSession: {
          token: response.session.token,
          expiresAt: response.session.expires_at,
        },
        scoreSession: null,
      }
      setClub(session)
    },
    [setClub],
  )

  const loginClub = React.useCallback<AuthContextValue['loginClub']>(
    async (input) => {
      const response = await authApi.clubLogin(input)
      applyLoginResponse(response)
      return response
    },
    [applyLoginResponse],
  )

  const loginDebugTestClub = React.useCallback<
    AuthContextValue['loginDebugTestClub']
  >(async () => {
    const response = await debugLogin()
    applyLoginResponse(response)
    if (response.score_session?.token) {
      const updated = updateScoreSession({
        token: response.score_session.token,
        expiresAt: response.score_session.expires_at,
      })
      dispatch({ type: 'set-club', session: updated })
    }
    return response
  }, [applyLoginResponse])

  const logoutClub = React.useCallback(() => {
    setClub(null)
  }, [setClub])

  const loginAdmin = React.useCallback<AuthContextValue['loginAdmin']>(
    async (input) => {
      const response = await authApi.adminLogin(input)
      const session: AdminSession = {
        accessToken: response.access_token,
        username: input.username,
        expiresAt: response.expires_at,
      }
      setAdmin(session)
    },
    [setAdmin],
  )

  const logoutAdmin = React.useCallback(() => {
    setAdmin(null)
  }, [setAdmin])

  const setScoreSession = React.useCallback<
    AuthContextValue['setScoreSession']
  >((token, expiresAt) => {
    const updated = updateScoreSession({ token, expiresAt })
    dispatch({ type: 'set-club', session: updated })
    return updated
  }, [])

  const clearScore = React.useCallback<AuthContextValue['clearScoreSession']>(
    () => {
      const updated = clearScoreSession()
      dispatch({ type: 'set-club', session: updated })
    },
    [],
  )

  const requestScoreReauth = React.useCallback<
    AuthContextValue['requestScoreReauth']
  >(() => {
    return new Promise<string | null>((resolve) => {
      dispatch({ type: 'open-score-reauth', resolve })
    })
  }, [])

  const resolveScoreReauth = React.useCallback<
    AuthContextValue['resolveScoreReauth']
  >(
    (token) => {
      const resolver = state.scoreReauthResolver
      dispatch({ type: 'close-score-reauth' })
      resolver?.(token)
    },
    [state.scoreReauthResolver],
  )

  const clubAuth = React.useCallback<AuthContextValue['clubAuth']>(() => {
    if (!state.club) return null
    return {
      accessToken: state.club.accessToken,
      providerSession: state.club.providerSession.token,
      scoreSession: state.club.scoreSession?.token ?? null,
    }
  }, [state.club])

  const value = React.useMemo<AuthContextValue>(
    () => ({
      club: state.club,
      admin: state.admin,
      loginClub,
      loginDebugTestClub,
      logoutClub,
      loginAdmin,
      logoutAdmin,
      setScoreSession,
      clearScoreSession: clearScore,
      requestScoreReauth,
      resolveScoreReauth,
      clubAuth,
      scoreReauthOpen: state.scoreReauthOpen,
    }),
    [
      state.club,
      state.admin,
      state.scoreReauthOpen,
      loginClub,
      loginDebugTestClub,
      logoutClub,
      loginAdmin,
      logoutAdmin,
      setScoreSession,
      clearScore,
      requestScoreReauth,
      resolveScoreReauth,
      clubAuth,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}

/**
 * Narrow hook for components that require a club session. Throws if called
 * from an unauthenticated tree — use inside routes protected by the club guard.
 */
export function useClubAuth(): ClubAuth & { clubname: string } {
  const auth = useAuth()
  if (!auth.club) {
    throw new Error(
      'useClubAuth: no active club session — this component must be behind the club guard.',
    )
  }
  return {
    accessToken: auth.club.accessToken,
    providerSession: auth.club.providerSession.token,
    scoreSession: auth.club.scoreSession?.token ?? null,
    clubname: auth.club.clubname,
  }
}
